import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch, useDebounce, useTranslation } from 'hooks';
import { IClientSearchData, searchClients, selectIsSearchClientsProcessing, selectSearchClientsResults } from 'store/clientManagement';
import { ChevronsDownIcon } from 'lucide-react';
import { cn } from 'lib/utils';
import { Card, CardHeader } from 'components/ui/card';
import { CardTitle } from 'components/ui/card';
import { Loading } from 'components/layout-atoms/Loading';
import { Button } from 'components/ui/button';
import { InfoCard } from 'components/layout-atoms/InfoCard';
import { ItemSelectedIcon } from 'components/common/_atoms/ItemSelectedIcon';
import { SearchBox } from 'components/common/_atoms/SearchBox';

interface IProps {
  selectedClient: IClientSearchData | null;
  onClientSelected: (client: IClientSearchData) => void;
}

export const ClientSelector = ({ selectedClient, onClientSelected }: IProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [searchValue, setSearchValue] = useState('')
  const debouncedSearchValue = useDebounce(searchValue, 300);

  const searchClientsResults = useSelector(selectSearchClientsResults);
  const isSearchClientsProcessing = useSelector(selectIsSearchClientsProcessing);


  useEffect(() => {
    dispatch(searchClients({ query: debouncedSearchValue, page: 1, limit: 10 }))
  }, [debouncedSearchValue, dispatch]);

  const handleLoadMore = () => {
    dispatch(searchClients({
      query: searchValue,
      page: parseInt(searchClientsResults?.page) + 1,
      limit: parseInt(searchClientsResults?.limit)
    }))
  }

  return (
    <>
      <SearchBox
        value={searchValue}
        setValue={setSearchValue}
      />

      <div className="grow overflow-auto">
        <div className='flex flex-col gap-3'>
          {
            !isSearchClientsProcessing && searchClientsResults?.data?.length === 0 && searchValue && (
              <InfoCard
                variant="item"
                title=''
                description={t('page.ClientsPage.search.noFilterData') as string}
              />
            )
          }

          {
            searchClientsResults?.data?.map(client => {
              const isSelected = client?.id === selectedClient?.id;

              return (
                <Card
                  key={client.id}
                  variant="item"
                  className={
                    cn(
                      'cursor-pointer', {
                      'border-primary': isSelected,
                      'opacity-50': isSearchClientsProcessing
                    })
                  }
                  onClick={() => onClientSelected(client)}
                >
                  <CardHeader className="p-3">
                    <CardTitle className="text-sm">{client?.firstName} {client?.lastName}</CardTitle>
                  </CardHeader>
                  {isSelected && (<ItemSelectedIcon />)}
                </Card>
              )
            })
          }

          {
            !isSearchClientsProcessing && searchClientsResults?.hasMore && (
              <Button
                variant="link"
                onClick={handleLoadMore}
              >
                {t('page.ClientsPage.clients.loadMore')}
                <ChevronsDownIcon className='size-4' />
              </Button>
            )
          }

          {
            isSearchClientsProcessing && (
              <Loading variant="item" className='border-transparent' />
            )
          }

        </div>
      </div>
    </ >
  );
};